import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { IMAGES } from '../shared/constants';
import LoginOrSignup from './LoginOrSignup';
import 'bootstrap/dist/css/bootstrap.min.css';
import './fielder_style.css';
import { Menu, X, Home, BarChart3, Building2, Tickets, Info, HelpCircle } from 'lucide-react';

// Top nav for >=768px; MobileTabBar covers narrower viewports unless `mobileMenu` is set.
const PublicNavbar = ({ mobileMenu = false }) => {
  const { isOperator } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const navRef = useRef(null);

  useEffect(() => setMenuOpen(false), [location.pathname]);

  useLayoutEffect(() => {
    if (!mobileMenu) return undefined;
    document.documentElement.classList.add('no-tab-bar');
    return () => document.documentElement.classList.remove('no-tab-bar');
  }, [mobileMenu]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const closeOutside = (e) => { if (!navRef.current?.contains(e.target)) setMenuOpen(false); };
    const closeOnEscape = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('pointerdown', closeOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [menuOpen]);

  const isActive = (path) => location.pathname === path ? 'fw-bold' : 'text-secondary';
  const getActiveStyle = (path) => location.pathname === path ? {color: 'var(--field-green)'} : {};

  return (
    <nav ref={navRef} className={`navbar navbar-expand-md navbar-light bg-white border-bottom sticky-top py-3 ${mobileMenu ? 'navbar-mobile-header' : 'd-none d-md-flex'}`}>
      <div className="container-fluid px-4 d-flex justify-content-between align-items-center">

        {/* 1. LOGO - Left */}
        <Link className="navbar-brand d-flex align-items-center" to="/">
          <img
            src={IMAGES.FIELDER_LOGO}
            alt="Fielder Logo"
            style={{ height: '36px' }}
          />
        </Link>

        {mobileMenu && (
          <button
            className="navbar-toggler border-0 p-0 shadow-none"
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-controls="navbarNav"
            aria-expanded={menuOpen}
            aria-label="Toggle navigation"
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        )}

        {/* 2. NAV CONTENT */}
        <div
          className={mobileMenu ? `collapse navbar-collapse ${menuOpen ? 'show' : ''}` : 'navbar-collapse'}
          id="navbarNav"
          onClick={(e) => { if (e.target.closest('a')) setMenuOpen(false); }}
        >

          {/* CENTER LINKS — mx-auto centers the list in the available space */}
          <ul className="navbar-nav mx-auto align-items-md-center gap-md-4 mt-3 mt-md-0">

            <li className="nav-item">
              <Link className={`nav-link d-flex align-items-center gap-2 ${isActive('/')}`} to="/" style={getActiveStyle('/')}>
                <Home size={18} />
                Home
              </Link>
            </li>

            {/* Operator-only links */}
            {isOperator ? (
              <>
                <li className="nav-item">
                  <Link className={`nav-link d-flex align-items-center gap-2 ${isActive('/bookings')}`} to="/bookings" style={getActiveStyle('/bookings')}>
                    <Tickets size={18} />
                    Bookings
                  </Link>
                </li>
                <li className="nav-item">
                  <Link className={`nav-link d-flex align-items-center gap-2 ${isActive('/organization')}`} to="/organization" style={getActiveStyle('/organization')}>
                    <Building2 size={18} />
                    Organization
                  </Link>
                </li>
                <li className="nav-item">
                  <Link className={`nav-link d-flex align-items-center gap-2 ${isActive('/operator-profile')}`} to="/operator-profile" style={getActiveStyle('/operator-profile')}>
                    <BarChart3 size={18} />
                    Analytics
                  </Link>
                </li>
              </>
            ) : (
              /* Renter links */
              <li className="nav-item">
                <Link className={`nav-link d-flex align-items-center gap-2 ${isActive('/my-bookings')}`} to="/my-bookings" style={getActiveStyle('/my-bookings')}>
                  <Tickets size={18} />
                  My Bookings
                </Link>
              </li>
            )}

            {mobileMenu && (
              <>
                <li className="nav-item d-md-none">
                  <Link className={`nav-link d-flex align-items-center gap-2 ${isActive('/about')}`} to="/about" style={getActiveStyle('/about')}>
                    <Info size={18} />
                    About
                  </Link>
                </li>
                <li className="nav-item d-md-none">
                  <Link className={`nav-link d-flex align-items-center gap-2 ${isActive('/help')}`} to="/help" style={getActiveStyle('/help')}>
                    <HelpCircle size={18} />
                    Help
                  </Link>
                </li>
              </>
            )}
          </ul>

          {/* RIGHT: LoginOrSignup */}
          <div className="d-flex align-items-center justify-content-md-center py-3 py-md-0">
            <LoginOrSignup />
          </div>

        </div>
      </div>
    </nav>
  );
}

export default PublicNavbar;
